import LoadingSpinner from 'components/core/LoadingSpinner/LoadingSpinner';
import Wrapper from 'components/layouts/Wrapper/Wrapper';
import { useParams } from 'react-router-dom';
import Headers from '../../../core/Headers/Headers';
import { Button, HeaderTabNavigation, Icon, ConfirmationModal } from 'components/core';
import { handleVendorsInnerTabNavData } from 'constants/TabNavData';
import {
  useGetVendorUsersQuery,
  useDeleteVendorUserMutation,
  useLoginAsVendorUserMutation,
  useGetVendorsInfoQuery,
  useMakeVendorMainUserMutation,
  useGetVendorCustomersQuery,
} from '../../../../services/vendorsApi';
import { getCurrentCustomerId } from '../../../../shared/configs/config';
import { isPermitted, useGetUserQuery } from '../../../../services/userApi';
import CreateUserModal from './CreateUser/CreateUser';
import { useRef, useState } from 'react';
import {
  canOfferMakeMain,
  isMainLogin,
  MainLoginCell,
  makeMainConfirmBody,
  makeMainConfirmTitle,
  makeMainErrorText,
  OTHER_COMPANIES_CHECKING,
  otherCompaniesNote,
} from './mainLogin';

/**
 * Primary UI component for user interaction
 */
const VendorUsersContainer = () => {
  const { id } = useParams();
  const [vendorsInnerTabNavData] = handleVendorsInnerTabNavData(id);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editUser, setEditUser] = useState<any>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [deleteUser, setDeleteUser] = useState<any>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [loginAsError, setLoginAsError] = useState<string | null>(null);
  const [loginAsUserId, setLoginAsUserId] = useState<string | null>(null);

  const { data: currentUser } = useGetUserQuery({});
  const canLoginAs = !!isPermitted(currentUser, 'login-as-vendor');

  // Main login = the login whose email equals the vendor account's email
  // (where PayUp sends digital checks and vendor notices). Unknown account
  // email -> no row is main and Make main stays hidden.
  const { data: vendorInfo } = useGetVendorsInfoQuery(id);
  const accountEmail: string | undefined = vendorInfo?.email || undefined;
  const canMakeMain = !!isPermitted(currentUser, 'make-main-login') && !!accountEmail;
  const [makeMainRow, setMakeMainRow] = useState<any>(null);
  const [makeMainError, setMakeMainError] = useState<string | null>(null);
  const [makeVendorMainUser, { isLoading: isMakingMain }] = useMakeVendorMainUserMutation();
  // Other companies that pay this vendor: read when the confirm opens.
  const {
    data: vendorCustomers,
    isFetching: isCheckingCustomers,
    isError: customersFailed,
  } = useGetVendorCustomersQuery(id ?? '', {
    skip: !makeMainRow || !id,
    refetchOnMountOrArgChange: true,
  });
  // Treat "no answer yet" as still checking so "could not check" never flashes.
  const customersPending = isCheckingCustomers || (!customersFailed && !vendorCustomers);
  const otherCompaniesLine = customersPending
    ? OTHER_COMPANIES_CHECKING
    : otherCompaniesNote(
        customersFailed ? null : vendorCustomers?.customers ?? null,
        getCurrentCustomerId()
      );

  // One POST at a time: the ref blocks a second click before React re-renders.
  const makeMainInFlight = useRef(false);
  const makeMainBlocked = customersPending || isMakingMain;
  const handleMakeMain = async () => {
    const userId = makeMainRow?.user?.id || makeMainRow?.id;
    if (!id || !userId || makeMainBlocked || makeMainInFlight.current) return;
    makeMainInFlight.current = true;
    setMakeMainError(null);
    try {
      await makeVendorMainUser({ accountId: id, userId }).unwrap();
      setMakeMainRow(null);
    } catch (error: any) {
      setMakeMainError(makeMainErrorText(error));
    } finally {
      makeMainInFlight.current = false;
    }
  };

  const { data, isLoading, isError, isSuccess } = useGetVendorUsersQuery(id);
  const [deleteUserMutation, { isLoading: isDeleting }] = useDeleteVendorUserMutation();
  const [loginAsVendorUser] = useLoginAsVendorUserMutation();

  const handleLoginAs = async (userRow: any) => {
    const userId = userRow?.user?.id || userRow?.id;
    if (!id || !userId) return;
    setLoginAsError(null);
    setLoginAsUserId(userId);
    try {
      const result = await loginAsVendorUser({ accountId: id, userId }).unwrap();
      if (result?.portalUrl) {
        window.open(result.portalUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (error: any) {
      setLoginAsError(error?.data?.message || error?.message || 'Failed to open login-as session.');
    } finally {
      setLoginAsUserId(null);
    }
  };

  return (
    <div>
      <Wrapper additionalClasses="flex-col items-end" pageTitle="Vendor Users" isBackButtonVisible>
        <Headers
          headingContent="Vendor Users"
          isButtonVisibled={false}
          headerWrapperClasses="border-none"
        />

        <HeaderTabNavigation tabNavData={vendorsInnerTabNavData} />

        <div className="flex flex-col w-full">
          <div className="flex flex-col w-full pt-6 mb-4 max-w-[1360px]">
            <div className="flex justify-between items-center mb-4">
              <Button
                onClick={() => {
                  setIsEditMode(false);
                  setEditUser(null);
                  setIsModalOpen(true);
                }}
                variants="default"
                buttonLabel={`Add User`}
              />
            </div>

            {loginAsError && <div className="mb-3 text-sm text-red-600">{loginAsError}</div>}

            {accountEmail && (
              <div className="mb-3 text-sm text-gray-700" data-testid="main-email-line">
                Checks and notices go to: <span className="font-medium">{accountEmail}</span>
              </div>
            )}

            {makeMainRow && (
              <ConfirmationModal
                mainContent={makeMainConfirmTitle(makeMainRow)}
                specialNote={[makeMainConfirmBody(makeMainRow), otherCompaniesLine]
                  .filter(Boolean)
                  .join(' ')}
                errorNote={makeMainError || undefined}
                confirmLabel="Make main"
                confirmDisabled={makeMainBlocked}
                handleSubmitEvent={handleMakeMain}
                handleBackEvent={() => {
                  setMakeMainRow(null);
                  setMakeMainError(null);
                }}
                isIconLoad={isMakingMain}
              />
            )}

            <CreateUserModal
              isOpen={isModalOpen}
              onClose={() => {
                setIsModalOpen(false);
                setIsEditMode(false);
                setEditUser(null);
              }}
              editUser={editUser}
              isEditMode={isEditMode}
              isEmailLocked={isEditMode && isMainLogin(editUser?.user?.email, accountEmail)}
            />

            {isDeleteModalOpen && (
              <ConfirmationModal
                mainContent={`Are you sure you want to remove ${
                  deleteUser?.user?.name ||
                  deleteUser?.user?.firstName + ' ' + deleteUser?.user?.lastName ||
                  'this user'
                }?`}
                handleSubmitEvent={async () => {
                  try {
                    const userId = deleteUser?.user?.id || deleteUser?.id;
                    await deleteUserMutation(userId).unwrap();
                    setIsDeleteModalOpen(false);
                    setDeleteUser(null);
                    setDeleteError(null);
                  } catch (error: any) {
                    console.error('Failed to delete user:', error);
                    const errorMessage =
                      error?.data?.message ||
                      error?.message ||
                      'Failed to delete user. Please try again.';
                    setDeleteError(errorMessage);
                  }
                }}
                handleBackEvent={() => {
                  setIsDeleteModalOpen(false);
                  setDeleteUser(null);
                  setDeleteError(null);
                }}
                isIconLoad={isDeleting}
                specialNote={deleteError || undefined}
              />
            )}

            {isLoading && <LoadingSpinner />}

            {isError && <div>Error loading vendor users</div>}

            {isSuccess && (
              <>
                {data?.users?.length > 0 ? (
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Email
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Phone
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Role
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Notifications Enabled
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {data?.users?.map((user: any, index: number) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {user.user?.name ||
                                user.user?.firstName + ' ' + user.user?.lastName ||
                                'N/A'}
                              {isMainLogin(user.user?.email, accountEmail) && (
                                <MainLoginCell isMain canMakeMain={false} onMakeMain={() => {}} />
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {user.user?.email || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {user.user?.phone || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {user.role || user.accountRole || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  user.user?.status === 'active' || user.user?.status === 'ACTIVE'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-red-100 text-red-800'
                                }`}
                              >
                                {user.user?.status || 'N/A'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <span
                                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  user?.notificationsEnabled === true
                                    ? 'bg-green-100 text-green-800'
                                    : user?.notificationsEnabled === false
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}
                              >
                                {user?.notificationsEnabled === true
                                  ? 'Enabled'
                                  : user?.notificationsEnabled === false
                                  ? 'Disabled'
                                  : 'N/A'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <div className="flex items-center space-x-2">
                                {canOfferMakeMain(user, accountEmail) && (
                                  <MainLoginCell
                                    isMain={false}
                                    canMakeMain={canMakeMain}
                                    busy={isMakingMain}
                                    onMakeMain={() => {
                                      setMakeMainError(null);
                                      setMakeMainRow(user);
                                    }}
                                  />
                                )}
                                {canLoginAs &&
                                  (user.user?.status === 'active' ||
                                    user.user?.status === 'ACTIVE') && (
                                    <button
                                      onClick={() => handleLoginAs(user)}
                                      disabled={loginAsUserId === (user.user?.id || user.id)}
                                      className="text-xs font-medium text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
                                      title="Login as user (read-only, 15 min)"
                                    >
                                      {loginAsUserId === (user.user?.id || user.id)
                                        ? 'Opening…'
                                        : 'Login as'}
                                    </button>
                                  )}
                                <button
                                  onClick={() => {
                                    setEditUser(user);
                                    setIsEditMode(true);
                                    setIsModalOpen(true);
                                  }}
                                  className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                                  title="Edit user"
                                >
                                  <Icon iconType="EditIcon" iconColor="inherit" />
                                </button>
                                <button
                                  onClick={() => {
                                    setDeleteUser(user);
                                    setDeleteError(null);
                                    setIsDeleteModalOpen(true);
                                  }}
                                  className="text-gray-400 hover:text-red-600 transition-colors duration-200"
                                  title="Remove user"
                                >
                                  <Icon iconType="RemoveTrashIcon" iconColor="inherit" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No users found for this vendor.</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </Wrapper>
    </div>
  );
};

export default VendorUsersContainer;
